import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { useData } from '../context/DataContext';
import { Product } from '../types';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import Breadcrumb from '../components/Breadcrumb';
import { trackEcommerce } from '../utils/analytics';

const ProductDetail: React.FC = () => {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const navigate = useNavigate();
  const { products } = useData();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Find product by ID
  const product = products.find(p => p.id === id);

  useEffect(() => {
    if (!product && products.length > 0) {
      // Redirect to products page if product not found
      navigate('/products');
    }
  }, [product, products, navigate]);  
  
  useEffect(() => {
      // Scroll to top when component mounts or category changes
      window.scrollTo(0, 0);
      
      // Track product view
      if (product) {
        trackEcommerce.viewItem({
          id: product.id,
          name: product.name,
          category: product.category,
          price: product.basePrice
        });
      }
    }, [product]);

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  // Combine primary image with additional images
  const images = [];
  if (product.image) {
    images.push(product.image);
  }
  if (product.images && product.images.length > 0) {
    images.push(...product.images);
  }

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Generate price display
  const getPriceDisplay = () => {
    if (!product.customizable && product.fixedSizes && product.fixedSizes.length > 0) {
      const minPrice = Math.min(...product.fixedSizes.map(s => s.price));
      return `Starting at $${minPrice.toFixed(2)}`;
    } else if (product.customizable && product.minWidth && product.minHeight) {
      const minPrice = (product.minWidth * product.minHeight / 144 * product.basePrice);
      return `Starting at $${minPrice.toFixed(2)}`;
    }
    return `$${product.basePrice.toFixed(2)}`;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title={product.name}
        description={product.description || `High-quality ${product.name} printing services. Custom sizes available. Fast turnaround in Montgomery County.`}
        keywords={`${product.name}, ${product.category} printing, custom ${product.name}`}
        image={product.image}
      />
      <StructuredData 
        type="Product"
        name={product.name}
        description={product.description || ''}
        image={product.image}
        price={product.basePrice}
      />

      <Breadcrumb 
        items={[
          { label: 'Products', path: '/products' },
          { label: product.category, path: `/products?category=${encodeURIComponent(product.category)}` },
          { label: product.name }
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Gallery */}
        <div>
          <div className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
            {images.length > 0 ? (
              <>
                <div className="relative w-full h-full">
                  {images.map((image, index) => (
                    <img 
                      key={index}
                      src={image} 
                      alt={`${product.name} - Image ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>
                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <span className="text-gray-500 text-xl">No Image Available</span>
              </div>
            )}
          </div>
          
          {/* Thumbnail Gallery */}
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                    index === currentImageIndex ? 'border-keystone-blue' : 'border-gray-200'
                  }`}
                >
                  <img 
                    src={image} 
                    alt={`${product.name} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-4xl font-bold mb-2">{product.name}</h1>
          <p className="text-xl text-gray-600 mb-6">{product.category}</p>
          
          <div className="mb-6">
            <p className="text-3xl font-bold text-keystone-blue">{getPriceDisplay()}</p>
            {product.customizable && (
              <p className="text-sm text-gray-600 mt-1">Price varies by size and options</p>
            )}
          </div>

          {product.description && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Description</h2>
              <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
            </div>
          )}

          {/* Size Information */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Size Options</h2>
            {product.customizable ? (
              <p className="text-gray-700">
                Custom sizes available from {product.minWidth}" × {product.minHeight}" 
                to {product.maxWidth}" × {product.maxHeight}"
              </p>
            ) : (
              product.fixedSizes && product.fixedSizes.length > 0 && (
                <ul className="space-y-2">
                  {product.fixedSizes.map((size, index) => (
                    <li key={index} className="flex justify-between items-center">
                      <span className="text-gray-700">
                        {size.width}" × {size.height}"
                        {size.note && <span className="text-sm text-gray-500 ml-2">({size.note})</span>}
                      </span>
                      <span className="font-semibold">${size.price.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>

          {/* Materials */}
          {product.materials && product.materials.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Available Materials</h2>
              <ul className="list-disc list-inside space-y-1">
                {product.materials.map((material) => (
                  <li key={material.id} className="text-gray-700">{material.name}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Finishing Options */}
          {product.finishingOptions && product.finishingOptions.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Finishing Options</h2>
              <ul className="list-disc list-inside space-y-1">
                {product.finishingOptions.map((option) => (
                  <li key={option.id} className="text-gray-700">{option.name}</li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA Button */}
          <Link
            to={`/quote?product=${product.id}`}
            className="inline-flex items-center justify-center w-full bg-keystone-gold text-keystone-blue font-bold py-3 px-6 rounded-lg hover:bg-keystone-gold-600 transition-colors"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Get Quote
          </Link>

          {/* Additional Info */}
          <div className="mt-6 space-y-2 text-sm text-gray-600">
            <p>✓ Fast turnaround time</p>
            <p>✓ High-quality printing</p>
            <p>✓ Competitive pricing</p>
            <p>✓ Local Montgomery County service</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;